/**
 * AiCallWizard - rich launch UI for a roster-tied AI (VAPI) call.
 *
 * Opened from the deal Call selector. Pick the persona (default, or an AI
 * Employee from the roster - the call runs AS that employee with its Voice
 * Profile + role), set an optional objective, and launch now or schedule it.
 * Posts to /api/deal-calls/launch-vapi; degrades to a clear "not configured"
 * line on 503. The server enforces the deal-safety recipient gate.
 */
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, X, Loader2, Phone, CalendarClock } from 'lucide-react';
import { cn } from '../../lib/utils.js';
import { api, ApiError } from '../../lib/api.js';
import { PARTY_TYPE_DISPLAY_DEFINITIONS } from '../../../../shared/party-types.js';

// Callable contact types the operator can confirm/correct at launch. Correcting
// it persists to the party (server-side) so the persona + future calls/drafts use it.
// Picker order, not pg_enum order: see PARTY_TYPE_DISPLAY_ORDER.
const CONTACT_TYPE_OPTIONS = PARTY_TYPE_DISPLAY_DEFINITIONS
  .filter((d) => d.callable)
  .map((d) => ({ value: d.value, label: d.label }));

interface AiEmployeeLite {
  id: string;
  name: string;
  description?: string | null;
  objectiveStatement?: string | null;
  triggerConfig?: Record<string, unknown> | null;
}

interface PartyLite {
  id: string;
  partyType: string;
  name?: string | null;
}

export function isAiCallConfigGap(err: unknown): boolean {
  const reason = err instanceof ApiError
    ? (err.data as { error?: string } | undefined)?.error
    : undefined;
  return err instanceof ApiError
    && (err.status === 503 || reason === 'vapi_not_configured' || reason === 'no_assistant_id');
}

function PersonaRow({ active, onClick, name, desc }: { active: boolean; onClick: () => void; name: string; desc: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-left px-2.5 py-1.5 rounded-md text-xs transition-colors flex items-center gap-2 border',
        active ? 'border-violet-400 bg-violet-50 dark:bg-violet-950/40' : 'border-transparent hover:bg-surface-sunken',
      )}
    >
      <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', active ? 'bg-violet-500' : 'bg-line')} />
      <span className="flex-1 min-w-0">
        <span className="block font-medium text-ink truncate">{name}</span>
        <span className="block text-[10px] text-ink-subtle truncate">{desc}</span>
      </span>
    </button>
  );
}

export default function AiCallWizard({
  dealId,
  partyId,
  partyPhone,
  identityId,
  overrideIdentityId,
  open,
  onClose,
}: {
  dealId: string;
  partyId?: string | null;
  partyPhone: string;
  /** Calling identity the dialer resolved for this member and deal. */
  identityId?: string;
  /** Set only when the operator deliberately dialed from someone else's line. */
  overrideIdentityId?: string;
  open: boolean;
  onClose: () => void;
}) {
  const [employeeId, setEmployeeId] = useState<string | null>(null); // null = default persona
  const [objective, setObjective] = useState('');
  const [when, setWhen] = useState<'now' | 'schedule'>('now');
  const [scheduledFor, setScheduledFor] = useState('');
  const [status, setStatus] = useState<{ kind: 'info' | 'error'; text: string } | null>(null);
  // null = "use the party's stored type" (no correction); a string = operator-corrected.
  const [contactTypeOverride, setContactTypeOverride] = useState<string | null>(null);

  // Load the deal's parties so we can show + confirm the recipient's CURRENT
  // contact type (the authoritative value, so the default is never a clobber).
  const partiesQuery = useQuery({
    queryKey: ['deal-parties', 'call-wizard', dealId],
    queryFn: async (): Promise<PartyLite[]> => {
      try {
        const res = await api.get<{ parties?: PartyLite[] }>(`/api/deals/${dealId}/parties`);
        return res.parties ?? [];
      } catch {
        return [];
      }
    },
    enabled: open && Boolean(partyId),
    staleTime: 60_000,
  });
  const recipientParty = partyId ? partiesQuery.data?.find((p) => p.id === partyId) ?? null : null;
  const storedContactType = recipientParty?.partyType ?? null;
  // What we show + send: the operator's correction, else the stored type.
  const effectiveContactType = contactTypeOverride ?? storedContactType;

  const roster = useQuery({
    queryKey: ['ai-employees', 'call-wizard'],
    queryFn: async (): Promise<AiEmployeeLite[]> => {
      try {
        const res = await api.get<{ employees?: AiEmployeeLite[] }>('/api/ai-employees');
        return (res.employees ?? []).map((e) => ({
          id: e.id,
          name: e.name,
          description: e.description ?? null,
          objectiveStatement: e.objectiveStatement ?? null,
          triggerConfig: e.triggerConfig ?? null,
        }));
      } catch {
        return [];
      }
    },
    enabled: open,
    staleTime: 60_000,
  });

  const objectiveTemplateFor = (employee?: AiEmployeeLite | null) => {
    const configTemplate = typeof employee?.triggerConfig?.defaultObjectiveTemplate === 'string'
      ? employee.triggerConfig.defaultObjectiveTemplate
      : null;
    return configTemplate ?? employee?.objectiveStatement ?? '';
  };

  const selectEmployee = (employee: AiEmployeeLite | null) => {
    const currentEmployee = roster.data?.find((item) => item.id === employeeId) ?? null;
    const currentTemplate = objectiveTemplateFor(currentEmployee).trim();
    const nextTemplate = objectiveTemplateFor(employee);
    setEmployeeId(employee?.id ?? null);
    if (!objective.trim() || (currentTemplate && objective.trim() === currentTemplate)) {
      setObjective(nextTemplate);
    }
  };

  const launch = useMutation({
    mutationFn: () => api.post('/api/deal-calls/launch-vapi', {
      dealThreadId: dealId,
      partyId: partyId ?? undefined,
      toNumber: partyPhone,
      identityId,
      overrideIdentityId,
      aiEmployeeId: employeeId ?? undefined,
      objective: objective.trim() || undefined,
      scheduledFor: when === 'schedule' && scheduledFor ? new Date(scheduledFor).toISOString() : undefined,
      // Only send a real correction (server persists when it differs from stored).
      contactType: contactTypeOverride && contactTypeOverride !== storedContactType ? contactTypeOverride : undefined,
    }),
    onSuccess: () => {
      setStatus({ kind: 'info', text: when === 'schedule' ? 'AI call scheduled.' : 'AI call launching.' });
      setTimeout(handleClose, 900);
    },
    onError: (err) => {
      const serverMsg = err instanceof ApiError
        ? (err.data as { message?: string } | undefined)?.message?.trim()
        : undefined;
      setStatus({
        kind: 'error',
        text: serverMsg
          ? serverMsg
          : isAiCallConfigGap(err)
            ? 'AI calling is not fully configured yet.'
            : 'Could not start the AI call.',
      });
    },
  });

  // Reset the contact-type correction on close so it never carries to a different
  // party the next time the wizard opens.
  const handleClose = () => {
    setContactTypeOverride(null);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-2 sm:items-center sm:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className="max-h-[92dvh] w-full max-w-md overflow-hidden rounded-xl border border-line bg-surface-raised shadow-xl"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="AI call"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-line">
              <div className="flex items-center gap-2 text-sm font-semibold text-ink">
                <Bot size={15} className="text-violet-600" /> AI call
              </div>
              <button type="button" onClick={handleClose} aria-label="Close" className="text-ink-muted hover:text-ink transition-colors">
                <X size={15} />
              </button>
            </div>

            <div className="max-h-[calc(92dvh-7.5rem)] space-y-4 overflow-y-auto p-4">
              <div className="text-xs text-ink-muted">
                Calling <span className="font-medium text-ink">{partyPhone}</span>. The AI speaks with the chosen persona and pursues your objective.
              </div>

              {partyId && (
                <div>
                  <label htmlFor="ai-call-contact-type" className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-1.5 block">
                    Contact type
                  </label>
                  <select
                    id="ai-call-contact-type"
                    value={effectiveContactType ?? ''}
                    onChange={(e) => setContactTypeOverride(e.target.value || null)}
                    disabled={partiesQuery.isFetching && !storedContactType}
                    className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-violet-400/40 disabled:opacity-60"
                  >
                    {!effectiveContactType && <option value="">Select a contact type</option>}
                    {CONTACT_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] text-ink-subtle">
                    {contactTypeOverride && contactTypeOverride !== storedContactType
                      ? 'This corrected type is saved to the contact and sets the call persona.'
                      : 'Confirm or correct who you are calling. This sets the AI persona.'}
                  </p>
                </div>
              )}

              <div>
                <div className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-1.5">Persona</div>
                <div className="space-y-1 max-h-44 overflow-y-auto">
                  <PersonaRow active={employeeId === null} onClick={() => selectEmployee(null)} name="Default AI persona" desc="General outbound assistant" />
                  {roster.data?.map((e) => (
                    <PersonaRow key={e.id} active={employeeId === e.id} onClick={() => selectEmployee(e)} name={`as ${e.name}`} desc={e.description ?? 'AI Employee'} />
                  ))}
                  {roster.isFetching && (
                    <div className="px-2 py-1 text-[10px] text-ink-subtle flex items-center gap-1">
                      <Loader2 size={10} className="animate-spin" /> loading roster
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-1.5 block">Objective (optional)</label>
                <textarea
                  value={objective}
                  onChange={(e) => setObjective(e.target.value)}
                  rows={2}
                  placeholder="e.g. Confirm their timeline and whether title is clear"
                  className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-violet-400/40"
                />
              </div>

              <div>
                <div className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-1.5">When</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setWhen('now')}
                    className={cn('flex-1 px-2 py-1.5 rounded-md text-xs font-medium border transition-colors', when === 'now' ? 'border-violet-400 bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300' : 'border-line text-ink-muted hover:bg-surface-sunken')}
                  >
                    Call now
                  </button>
                  <button
                    type="button"
                    onClick={() => setWhen('schedule')}
                    className={cn('flex-1 px-2 py-1.5 rounded-md text-xs font-medium border transition-colors', when === 'schedule' ? 'border-violet-400 bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300' : 'border-line text-ink-muted hover:bg-surface-sunken')}
                  >
                    Schedule
                  </button>
                </div>
                {when === 'schedule' && (
                  <input
                    type="datetime-local"
                    value={scheduledFor}
                    onChange={(e) => setScheduledFor(e.target.value)}
                    className="mt-2 w-full rounded-md border border-line bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-violet-400/40"
                  />
                )}
              </div>

              {status && (
                <div className={cn('rounded-md border px-2.5 py-1.5 text-[11px]', status.kind === 'error' ? 'bg-danger/10 border-danger/30 text-danger-fg dark:text-red-200' : 'bg-info-bg border-info/30 text-info')}>
                  {status.text}
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-line px-4 py-3 sm:flex-row sm:items-center sm:justify-end">
              <button type="button" onClick={handleClose} className="w-full rounded-md px-3 py-2 text-xs text-ink-muted transition-colors hover:bg-surface-sunken sm:w-auto sm:py-1.5">Cancel</button>
              <button
                type="button"
                onClick={() => { setStatus(null); launch.mutate(); }}
                disabled={launch.isPending || (when === 'schedule' && !scheduledFor)}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-violet-600 px-3 py-2 text-xs font-medium text-violet-50 transition-colors hover:bg-violet-700 disabled:opacity-50 sm:w-auto sm:py-1.5"
              >
                {launch.isPending ? <Loader2 size={12} className="animate-spin" /> : when === 'schedule' ? <CalendarClock size={12} /> : <Phone size={12} />}
                {when === 'schedule' ? 'Schedule call' : 'Call now'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
